/**
 * 블로그 글 상세의 JSON-LD — `BlogPosting` 하나.
 *
 * 값은 라우트가 서버에서 받은 글 문서에서만 온다. 글쓴이가 없거나 운영 주체 이름이면 운영 주체(Organization)가 저자다.
 * 수정일은 `contentModifiedAt`(고친 날, 없으면 작성일) — `updatedAt` 은 조회 시각일 수 있어 쓰지 않는다.
 */

import type { JsonLdObject } from 'src/components/json-ld';

import { removeHtmlTags } from 'src/utils/text/html-text';
import { contentModifiedAt } from 'src/utils/seo/content-dates';

import { SITE_URL, SITE_NAME, SITE_NAME_EN, ORGANIZATION_ID } from 'src/constants/site-identity';

import type { BlogPost } from '../scope/blog-post-scope';

// ----------------------------------------------------------------------

/** 검색 결과 요약과 같은 길이 */
const DESCRIPTION_MAX = 160;

/** 문단이 끝나는 자리 — 태그를 걷기 전에 공백으로 바꿔야 앞뒤 문장이 붙지 않는다 */
const BLOCK_BOUNDARY = /<\/(?:p|div|li|h[1-6]|blockquote)>|<(?:br|hr)\b[^>]*>/gi;

/** 요약 앞머리의 장식 기호(인용 표시 `|` · 글머리 기호) */
const LEADING_MARKS = /^[\s|·•>\-–—]+/;

/**
 * @param post 서버가 받은 글 — `createdAt` 이 없으면 `datePublished` 를 생략한다
 * @returns 공개 주소 `/post/{id}` 를 mainEntityOfPage 로 삼는 BlogPosting
 */
export function buildBlogPostingJsonLd(post: BlogPost): JsonLdObject {
  const url = `${SITE_URL}/post/${post.id}`;
  const published = toIso(post.createdAt);
  const modified = toIso(contentModifiedAt(post));

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline: post.title ?? '',
    description: summarizeBlogPost(post),
    inLanguage: 'ko',
    ...(post.coverUrl ? { image: post.coverUrl } : {}),
    ...(published ? { datePublished: published } : {}),
    ...(modified ? { dateModified: modified } : {}),
    ...(post.tags?.length ? { keywords: post.tags.join(', ') } : {}),
    author: isPersonAuthor(post.author?.name)
      ? { '@type': 'Person', name: post.author!.name }
      : { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
  };
}

/**
 * 요약 필드가 있으면 그것, 없으면 본문을 문단 사이 공백을 살려 글자로 편 앞 160자 — 메타 description 도 이것을 쓴다.
 * 앞머리의 장식 기호는 뗀다.
 */
export function summarizeBlogPost(post: BlogPost): string {
  const raw = post.description?.trim() || removeHtmlTags(String(post.content ?? '').replace(BLOCK_BOUNDARY, ' '));
  const source = raw.replace(LEADING_MARKS, '');
  return source.length > DESCRIPTION_MAX ? `${source.slice(0, DESCRIPTION_MAX)}...` : source;
}

/** 글쓴이 이름이 사람인가 — 비었거나 운영 주체 이름(「캐치모먼츠」·「CatchMoments」)이면 아니다 */
function isPersonAuthor(name: string | undefined): boolean {
  const trimmed = name?.trim().toLowerCase();
  return Boolean(trimmed) && trimmed !== SITE_NAME.toLowerCase() && trimmed !== SITE_NAME_EN.toLowerCase();
}

function toIso(value: Date | null | undefined): string | null {
  return value instanceof Date && Number.isFinite(value.getTime()) ? value.toISOString() : null;
}
